import {produce} from 'immer';

import {assert} from 'Asserts';
import {BenchlingAction} from 'redux-stores/Action';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ClientSchemaMapperConfig} from '../../FlowchartConfigConstants';
import {
  CustomMappingData,
  StaticTemplateFlowchart,
  TransitionSource,
} from '../processFlowchartConfigEditorConstants';
import {FlowchartConfigActionType} from './flowchartConfigActions';

export interface FlowchartInteractionState {
  selectedNodeIds: Set<string>;
  selectedEdgeIds: Set<string>;
  edgeLabelsByEdgeId: Record<string, string>;
}

export const IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT = true;
export const IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT = false;
export interface FlowchartConfigMetadataOptions {
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
}

export interface FlowchartConfigMetadata {
  // TODO: These mapper configs don't currently get used, we re-build from data flow edges instead.
  // Need to address for ACM/PCM decision.
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  // Can only be null during initialization
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  interactionState: FlowchartInteractionState;
  // The lastest version id of the flowchart config, used to determine staleness
  currentVersionId: string | null;
  options: FlowchartConfigMetadataOptions;
}

// Base platform pause conditions
enum BasePauseCondition {
  HAS_VALIDATION_ERRORS = 'HAS_VALIDATION_ERRORS',
  HAS_PLACEHOLDER_NODE = 'HAS_PLACEHOLDER_NODE',
}

export enum ProcedurePauseCondition {
  IS_EDITING_PARAMETERS = 'IS_EDITING_PARAMETERS',
}

// Union of all pause conditions (teams can add their own enums as a union here)
export type FlowchartConfigAutosaverPausedConditionType = BasePauseCondition | ProcedurePauseCondition;

// Convenience object with all conditions, can use like an enum
// Ex: FlowchartConfigAutosaverPausedCondition.HAS_VALIDATION_ERRORS
// Ex: Object.values(FlowchartConfigAutosaverPausedCondition) to get all conditions
export const FlowchartConfigAutosaverPausedCondition = {
  ...BasePauseCondition,
  ...ProcedurePauseCondition,
} as const;

export interface SingleFlowchartConfigReduxData {
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  /**
   * Properties that downstream components of the FlowchartConfigEditor want to change to control
   * autosaver behavior, or to access to determine their own state.
   */
  componentLiveProperties: {
    /**
     * The autosaver can be PAUSED as a temporary UX measure. E.g., during sensitive edits that need
     * to own the draft state without a server call. If any conditions present, the autosaver will be paused.
     *
     * Downstream components own this state, and are fully responsible for managing it.
     */
    autosaverPausedConditions: Set<FlowchartConfigAutosaverPausedConditionType>;
    isDirty: boolean;
    isReadOnly: boolean;
  };
  /**
   * Properties that the autosaver platform controls/access, that downstream components do not need to engage with.
   */
  platformLiveProperties: {
    isSaveInFlight: boolean;
    /**
     * The autosaver is considered BLOCKED if there is a fundamental (not temporary) issue. E.g., the
     * flowchart config is blocked on a server error, etc.
     *
     * This is not something that downstream components should change.
     */
    isAutosaverBlocked: boolean;
    /**
     * The last error that occurred during save, if any. Used to display error state in save indicator.
     */
    lastSaveError: Error | null;
  };
}

export type FlowchartConfigsState = Record<string, SingleFlowchartConfigReduxData>;

/**
 * Helper function to check if the autosaver should be paused (when 0 conditions present).
 */
export function hasAnyAutosaverPausedConditions(
  autosaverPausedConditions: Set<FlowchartConfigAutosaverPausedConditionType>
): boolean {
  return autosaverPausedConditions.size > 0;
}

export const FLOWCHART_CONFIGS_INITIAL_STATE: FlowchartConfigsState = {};

// If no existing state is found for a flowchart config, return a seed empty state
export const getSingleFlowchartConfigState = (
  existingData: SingleFlowchartConfigReduxData | undefined
): SingleFlowchartConfigReduxData =>
  existingData ?? {
    staticFlowchart: {nodesStaticData: [], edgesStaticData: []},
    flowchartConfigMetadata: {
      schemaMapperConfigs: [],
      rootTaskSchema: null,
      customMappingDataByEdgeId: {},
      transitionSourceByNodeIdPair: {},
      interactionState: {
        selectedNodeIds: new Set([]),
        selectedEdgeIds: new Set([]),
        edgeLabelsByEdgeId: {},
      },
      currentVersionId: null,
      options: {
        isFlowchartModifiableOnCreation: IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
        isFlowchartModifiableAfterCreation: IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
      },
    },
    componentLiveProperties: {
      isDirty: false,
      isReadOnly: false,
      autosaverPausedConditions: new Set([]),
    },
    platformLiveProperties: {
      isSaveInFlight: false,
      isAutosaverBlocked: false,
      lastSaveError: null,
    },
  };

/**
 * Validates that root task schema requirements are met and prevents changes after initial setting.
 */
function validateRootTaskSchemaChanges(
  currentRootTaskSchema: {id: string} | null,
  incomingRootTaskSchema: {id: string} | null | undefined
): void {
  // Ensure we have everything we need in here before we apply partial
  // Allow setting rootTaskSchema for the first time, or if it's already set
  const hasRootTaskSchema = currentRootTaskSchema != null || incomingRootTaskSchema != null;
  assert(hasRootTaskSchema, 'Root task schema is required');

  // Prevent changing the root task schema after initial setting
  if (currentRootTaskSchema != null && incomingRootTaskSchema != null) {
    assert(
      currentRootTaskSchema.id === incomingRootTaskSchema.id,
      'Root task schema cannot be changed after initial setting'
    );
  }
}

export default function reduceFlowchartConfigs(
  state: FlowchartConfigsState = FLOWCHART_CONFIGS_INITIAL_STATE,
  action: BenchlingAction
): FlowchartConfigsState {
  switch (action.type) {
    case FlowchartConfigActionType.UPDATED_FLOWCHART_CONFIG:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        if (action.staticFlowchart != null) {
          draftState[action.flowchartConfigId].staticFlowchart = action.staticFlowchart;
        }
        if (action.flowchartConfigMetadata != null) {
          const currentMetadata = draftState[action.flowchartConfigId];
          const currentRootTaskSchema = currentMetadata?.flowchartConfigMetadata?.rootTaskSchema;
          const incomingRootTaskSchema = action.flowchartConfigMetadata.rootTaskSchema;

          validateRootTaskSchemaChanges(currentRootTaskSchema, incomingRootTaskSchema);

          // Deep merge interactionState if it's being updated
          const mergedInteractionState = action.flowchartConfigMetadata.interactionState
            ? {
                ...currentMetadata.flowchartConfigMetadata.interactionState,
                ...action.flowchartConfigMetadata.interactionState,
              }
            : currentMetadata.flowchartConfigMetadata.interactionState;

          draftState[action.flowchartConfigId].flowchartConfigMetadata = {
            ...currentMetadata.flowchartConfigMetadata,
            ...action.flowchartConfigMetadata,
            interactionState: mergedInteractionState,
          };
        }
      });

    case FlowchartConfigActionType.UNMOUNTED_FLOWCHART_CONFIG_EDITOR:
      return produce(state, (draftState) => {
        delete draftState[action.flowchartConfigId];
      });

    case FlowchartConfigActionType.DIRTY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].componentLiveProperties.isDirty = action.value;
      });

    case FlowchartConfigActionType.READONLY_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].componentLiveProperties.isReadOnly = action.value;
      });

    case FlowchartConfigActionType.IS_SAVE_IN_FLIGHT_STATE_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        const platformState = draftState[action.flowchartConfigId].platformLiveProperties;
        const componentState = draftState[action.flowchartConfigId].componentLiveProperties;

        const wasInFlight = platformState.isSaveInFlight;
        const nowInFlight = action.isSaveInFlight;

        // Make idempotent: If already in the target state and no error is being set, do nothing
        // When there is an exception the AutosaveManager will always call this action in a finally block,
        // but the app may have already handled it in order to set the lastSaveError
        if (wasInFlight === nowInFlight && action.error === undefined) {
          return;
        }

        // Automatically manage dirty state based on save lifecycle:
        // - When save starts: clear dirty and lastSaveError
        // - When save ends with error: set dirty=true (we'll retry), set lastSaveError
        // - When save ends successfully: dirty stays false, lastSaveError stays null

        if (!wasInFlight && nowInFlight) {
          // Save is starting: optimistically clear dirty and any previous errors
          componentState.isDirty = false;
          platformState.lastSaveError = null;
        } else if (wasInFlight && !nowInFlight && action.error !== undefined) {
          // Save failed: mark as dirty so autosaver will retry
          componentState.isDirty = true;
          platformState.lastSaveError = action.error;
        }

        platformState.isSaveInFlight = nowInFlight;
      });

    case FlowchartConfigActionType.AUTOSAVER_PAUSED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        // Note, immer makes these operations safe (maintains immutability of our "state" object with set operations)
        if (action.value) {
          draftState[action.flowchartConfigId].componentLiveProperties.autosaverPausedConditions.add(
            action.condition
          );
        } else {
          draftState[action.flowchartConfigId].componentLiveProperties.autosaverPausedConditions.delete(
            action.condition
          );
        }
      });

    case FlowchartConfigActionType.AUTOSAVER_BLOCKED_CHANGED:
      return produce(state, (draftState) => {
        draftState[action.flowchartConfigId] = getSingleFlowchartConfigState(
          draftState[action.flowchartConfigId]
        );
        draftState[action.flowchartConfigId].platformLiveProperties.isAutosaverBlocked = action.value;
      });

    default:
      return state;
  }
}
